import { productPricingService } from "../../services/productPricingService";
import { Price, PRICE_UPDATE_EVENT } from "../../types/pricing/price-display.types";
import { EstimatedPrice, ProductPriceDetails, QuantityMetaData } from "../../types/pricing/product-price-details.types";

export type ProductPriceData = {
  totalListPrice: Price;
  totalDiscountedPrice: Price;
  currency: string;
  quantity: number;
};

export const priceEventTarget = new EventTarget();

const getPriceEntry = (
  estimatedPrices: Record<number, EstimatedPrice>,
  quantityMetaData?: QuantityMetaData,
) => {
  const { defaultQuantity, minimumOrderableQuantity } = quantityMetaData || {};
  const quantity =
    minimumOrderableQuantity ??
    defaultQuantity ??
    Number(Object.keys(estimatedPrices)[0]);

  const priceEntry = estimatedPrices[quantity];
  if (priceEntry) return { priceEntry, quantity };

  // Fallback to first available price entry
  const firstAvailableEntry = Object.entries(estimatedPrices)[0];
  if (firstAvailableEntry) {
    const [fallbackQuantity, fallbackEntry] = firstAvailableEntry;
    return { priceEntry: fallbackEntry, quantity: Number(fallbackQuantity) };
  }

  return undefined;
};

export const getProductPriceData = (
  productId: string,
  timeoutMs = 5000,
): Promise<ProductPriceData> => {
  if (!productId?.trim()) {
    return Promise.reject(new Error("Invalid productId"));
  }

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error("Price data unavailable after timeout"));
    }, timeoutMs);

    const cleanup = () => {
      clearTimeout(timeoutId);
      priceEventTarget.removeEventListener(PRICE_UPDATE_EVENT, listener);
    };

    const listener = (event: Event) => {
      const { detail } = event as CustomEvent<Record<string, ProductPriceDetails>>;
      tryResolveFromPriceDetails(detail);
    };

    const tryResolveFromPriceDetails = (productPriceDetails: Record<string, ProductPriceDetails>) => {
      const product = Object.values(productPriceDetails).find(
        (entry) => entry?.productId === productId,
      );
      if (!product) return false;

      const estimatedPrices = product.prices?.estimatedPrices || {};
      const priceEntryResult = getPriceEntry(estimatedPrices, product.quantityMetaData);

      if (!priceEntryResult) return false;

      const { priceEntry, quantity } = priceEntryResult;
      const { totalListPrice, totalDiscountedPrice } = priceEntry;
      if (totalListPrice && totalDiscountedPrice) {
        resolve({
          totalListPrice,
          totalDiscountedPrice,
          currency: product.prices.currency,
          quantity: quantity,
        });
        cleanup();
      }
      return true;
    };

    // Check immediately with current state
    if (tryResolveFromPriceDetails(productPricingService.pricingDetails)) {
      return;
    }

    // Listen for future updates
    priceEventTarget.addEventListener(PRICE_UPDATE_EVENT, listener);
  });
};
